import React, { FC, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import APP1 from '../../../../app/assets/images/projectImage/bitEuImages/app1.png'
import APP2 from '../../../../app/assets/images/projectImage/bitEuImages/app2.png'
import LOGO from '../../../../app/assets/images/projectImage/bitEuImages/logo.png'

interface BitEuProjectOverviewProps {
    bitEuProject: any;
}

const BitEuProjectOverview: FC<BitEuProjectOverviewProps> = ({ bitEuProject }) => {
    const { projectOverviewContent } = bitEuProject?.bitEuProjectDetails;
    // Animation variants
    const fadeIn = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.8 } }
    }

    const slideInFromLeft = {
        hidden: { x: -100, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
    }

    const slideInFromRight = {
        hidden: { x: 100, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
    }

    const slideInFromBottom = {
        hidden: { y: 100, opacity: 0 },
        visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
    }

    const slideInFromTop = {
        hidden: { y: -100, opacity: 0 },
        visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
    }

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    }

    const renderDescription = (desc: any) => {
        if (!desc) return null;
        if (typeof desc === 'string') return <div className='text-[#e0e0e0] pt-2 text-base sm:text-lg'>{desc}</div>;
        if (Array.isArray(desc)) {
            return (
                <ul className='list-disc ml-6 text-[#e0e0e0] space-y-2'>
                    {desc.map((d: string, i: number) => (
                        <li key={i} className='text-sm sm:text-base'>{d}</li>
                    ))}
                </ul>
            )
        }
        if (typeof desc === 'object') {
            return (
                <div>
                    {desc.intro && <div className='text-[#e0e0e0] pt-2 text-base sm:text-lg mb-3'>{desc.intro}</div>}
                    {Array.isArray(desc.bullets) && (
                        <ul className='list-disc ml-6 text-[#e0e0e0] space-y-2'>
                            {desc.bullets.map((d: string, i: number) => (
                                <li key={i} className='text-sm sm:text-base'>{d}</li>
                            ))}
                        </ul>
                    )}
                </div>
            )
        }
        return <div className='text-[#e0e0e0] pt-2 text-base sm:text-lg'>{String(desc)}</div>;
    }

    // Create refs for scroll-triggered animations
    const secondSectionRef = useRef(null);

    // Use useInView hook for section
    const secondSectionInView = useInView(secondSectionRef, { once: true, amount: 0.2 });

    return (
        <div>
            <motion.div
                className='bg-bitEu-project-bg bg-center bg-cover bg-no-repeat'
                initial="hidden"
                animate="visible"
                variants={fadeIn}
            >
                <div className='bg-blackTransparent'>
                    <div className='flex flex-col md:flex-row gap-8 md:gap-4 justify-between items-center md:p-4 md:pr-0 '>
                        <motion.div
                            className='flex flex-col px-4 sm:px-8 sm:py-8 md:pl-12 xl:pl-32 w-full md:w-[50%] lg:w-[40%] gap-16 sm:gap-24 md:gap-26 xl:gap-30'
                            variants={staggerContainer}
                            initial="hidden"
                            animate="visible"
                        >
                            {projectOverviewContent.map((item: any) => {
                                return (
                                    <motion.div variants={slideInFromLeft} key={item?.key}>
                                        <div className='text-2xl sm:text-3xl font-bold'>{item?.title}</div>
                                        {renderDescription(item?.description)}
                                    </motion.div>
                                )
                            })}
                        </motion.div>
                        <motion.div
                            className='w-full md:w-[45%] md:px-4 px-0 md:pr-0 flex justify-center'
                            variants={slideInFromRight}
                            initial="hidden"
                            animate="visible"
                        >
                            <motion.img
                                src={APP1}
                                className='max-w-full h-auto'
                                alt="Application screenshot"
                                whileHover={{
                                    scale: 1.05,
                                    transition: { duration: 0.3 }
                                }}
                            />
                        </motion.div>
                    </div>
                </div>
            </motion.div>

            <motion.div
                className='bg-[#dccb63] px-4 md:px-8 lg:px-16 xl:px-48 flex flex-col md:flex-row items-center justify-center gap-6 sm:gap-8 md:gap-12 relative min-h-[15rem] md:h-[20rem] mt-16  md:mt-5 xl:mt-48 py-12 md:py-0'
                ref={secondSectionRef}
                initial="hidden"
                animate={secondSectionInView ? "visible" : "hidden"}
                variants={fadeIn}
            >
                <motion.div
                    className='absolute z-20 right-4 -top-20 sm:-top-28 md:-top-40 w-20 sm:w-24 md:w-auto'
                    variants={slideInFromTop}
                    initial="hidden"
                    animate={secondSectionInView ? "visible" : "hidden"}
                >
                    <motion.img
                        src={LOGO}
                        className='max-w-full h-auto'
                        alt="Logo"
                        whileHover={{ rotate: 15, transition: { duration: 0.3 } }}
                    />
                </motion.div>
                {/* <Lottie animationData={test} loop={true} /> */}
                <motion.img
                    src={APP2}
                    className='relative xl:-bottom-8 max-w-full h-auto w-60 sm:w-72 xl:w-auto'
                    alt="Application screenshot"
                    variants={slideInFromBottom}
                    initial="hidden"
                    animate={secondSectionInView ? "visible" : "hidden"}
                    whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
                />
                {/* <video src={video} autoPlay muted loop className='bg-transparent' /> */}

                <motion.div
                    className='text-black w-full px-4 md:px-0'
                    variants={slideInFromRight}
                    initial="hidden"
                    animate={secondSectionInView ? "visible" : "hidden"}
                >
                    <div className='text-2xl lg:text-3xl font-bold'>Features</div>
                    <div className='pt-2 text-base lg:text-lg'>BIT.EU supports over 350 cryptocurrencies, offering traders a diverse and flexible trading experience. Users can access features like spot trading, futures contracts, and copy trading to match their strategy and skill level. The platform includes multi-language support, making it accessible worldwide, and ensures top-tier security with 2FA and a strict KYC process. With a sleek, responsive interface and powerful backend, BIT.EU delivers fast, seamless trading across web and mobile devices—no matter how active the market gets.</div>
                </motion.div>
            </motion.div>
        </div>
    )
}

export default BitEuProjectOverview
